import { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Lightweight collapsible used on Application Review pages to keep the
 * vertical density low (especially on mobile). Closed by default; click the
 * header to expand. The `summary` prop is shown next to the title when
 * collapsed so users still get the gist before opening.
 *
 * Usage:
 *   <Collapsible label="Cover letter" summary="247 words, tailored voice" testId="cl">
 *     <pre>{coverLetterText}</pre>
 *   </Collapsible>
 */
export default function Collapsible({
  label,
  summary,
  defaultOpen = false,
  accent = "primary", // "primary" | "amber" | "green"
  testId,
  children,
  rightSlot,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const accentClass =
    accent === "amber"
      ? "text-accent-amber"
      : accent === "green"
      ? "text-accent-green"
      : "text-primary";

  return (
    <div className="bg-background border border-border" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-hover transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className={`terminal-label ${accentClass}`}>[ {label.toUpperCase()} ]</div>
          {summary && !open && (
            <div className="text-xs text-foreground/70 mt-1.5 line-clamp-2 leading-snug">{summary}</div>
          )}
        </div>
        {rightSlot && <div onClick={(e) => e.stopPropagation()}>{rightSlot}</div>}
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
}
